import {Chip, CircularProgress} from '@mui/material'
import {envImagePath, serverLocalName} from 'GLOBAL/envVariables'
import dayjs from 'dayjs'
import {showTemperatureHandler} from 'utils/const/globalController'
import {
  globalFormatWithHyphens,
  globalSerialNumberFormatWithHyphens,
} from 'utils/const/globalConst'

interface Props {
  rows: any[]
  loading: boolean
  cooperInfo: any
  officeInfo: any
  onImageDetailOpen: (row: any) => void
  onPhotoOpen: (row: any) => void
  onCustomerDetailOpen: (row: any) => void
  usePhotoModal: boolean
}

const CustomerStatusMobileCard = ({
  rows,
  loading,
  cooperInfo,
  officeInfo,
  onImageDetailOpen,
  onPhotoOpen,
  onCustomerDetailOpen,
  usePhotoModal,
}: Props) => {
  if (loading) {
    return (
      <div className='flex justify-center p-8'>
        <CircularProgress />
      </div>
    )
  }

  return (
    <div className='flex flex-col gap-2 mt-2'>
      {rows.map((row) => {
        const customerNum = globalFormatWithHyphens(row.officeId, row.customerNumber)
        const serialNum = globalSerialNumberFormatWithHyphens(row.serialNumber)
        const cooperIdx = cooperInfo?.value
        const imageSrc = row.filePath ? `${envImagePath}${row.filePath}` : null
        const isRemoved = row.customerStatus === 'remove'
        const showBattery = cooperIdx === 1 || cooperIdx === 0

        // 검침값 (status 컬럼 로직과 동일)
        const isBlank = row.processStatus === 'NONE' && row.recognizedType === 'PRV'
        const noCheckValue = row.checkValue === '' || row.checkValue === null
        const decimal =
          row.checkDecimalValue === '' || row.checkDecimalValue === null
            ? ''
            : '.' + row.checkDecimalValue?.split('.')[1]

        return (
          <div
            key={row.id}
            className='border rounded-lg shadow-sm border-slate-300 bg-white overflow-hidden'
          >
            {/* 헤더: 수용가번호 + 철거 상태 / 우측 끝 사업소 */}
            <div
              className='flex items-center gap-2 px-4 py-3 border-b border-slate-300 bg-slate-50 cursor-pointer'
              onClick={() => (usePhotoModal ? onPhotoOpen(row) : onCustomerDetailOpen(row))}
            >
              <span className='text-blue-700 font-bold text-3xl'>{customerNum}</span>
              {isRemoved && (
                <Chip label='철거' color='error' sx={{fontSize: '1rem'}} className='shrink-0' />
              )}
              {row.officeName && (
                <span className='text-lg text-slate-700 ml-auto min-w-0 truncate'>
                  {row.officeName}
                </span>
              )}
            </div>

            {/* 본문: 좌측 정보 + 우측 썸네일 */}
            <div className='flex gap-3 p-4'>
              <div className='flex-1 min-w-0 flex flex-col gap-1.5'>
                {/* 이름 */}
                <div className='text-2xl font-bold text-slate-900 line-clamp-2'>
                  {row.customerName}
                </div>

                {/* 주소 */}
                <div className='text-xl text-slate-800 line-clamp-2'>
                  <span className='text-blue-600 font-bold'>도로명</span> {row.addressRoad}
                </div>
                {row.addressNumber && (
                  <div className='text-xl text-slate-800 line-clamp-2'>
                    <span className='text-orange-600 font-bold'>지번</span> {row.addressNumber}
                  </div>
                )}
                {row.addressDetail && (
                  <div className='text-xl text-slate-800 line-clamp-2'>
                    <span className='font-bold'>상세</span> {row.addressDetail}
                  </div>
                )}
                {row.managerName && row.managerName !== 'NULL' && (
                  <div className='text-xl text-slate-700 line-clamp-2'>
                    담당자: {row.managerName}
                  </div>
                )}

                {/* 단말기 SN + 배터리 (철거는 헤더 칩으로 표기) */}
                {!isRemoved && (
                  <div className='flex items-center gap-2 text-xl mt-1 flex-wrap'>
                    <span className='text-slate-800'>{serialNum}</span>
                    {showBattery && row.batteryLevel != null && (
                      <span
                        className={`font-semibold ${
                          row.batteryLevel < 30 ? 'text-red-600' : 'text-slate-700'
                        }`}
                      >
                        배터리 {row.batteryLevel}%
                      </span>
                    )}
                  </div>
                )}

                {/* 계량기/단말기 상태 + 구경 + 온도 */}
                <div className='flex items-center gap-x-4 gap-y-1 mt-1 text-xl flex-wrap'>
                  <div className='flex items-center gap-1'>
                    <span className='text-slate-700'>계량기</span>
                    <span
                      className={`font-bold ${row.efosStatus ? 'text-green-700' : 'text-red-600'}`}
                    >
                      {row.efosStatus ? '정상' : '점검'}
                    </span>
                  </div>
                  <div className='flex items-center gap-1'>
                    <span className='text-slate-700'>단말기</span>
                    <span
                      className={`font-bold ${row.deviceStatus ? 'text-green-700' : 'text-red-600'}`}
                    >
                      {row.deviceStatus ? '정상' : '점검'}
                    </span>
                  </div>
                  {row.meterDiameter && <span className='text-slate-700'>{row.meterDiameter}mm</span>}
                  {row.temperature !== 99 &&
                    row.temperature != null &&
                    showTemperatureHandler(serverLocalName, officeInfo?.value) && (
                      <span className='text-slate-700'>{row.temperature}°C</span>
                    )}
                </div>

                {/* 검침값 + 검침일 */}
                <div className='flex items-center gap-2 mt-1 text-xl flex-wrap'>
                  <span className='text-slate-700'>검침값</span>
                  {isBlank ? (
                    <span className='text-slate-800'>-</span>
                  ) : noCheckValue ? (
                    <Chip label='인식불가' size='small' color='error' />
                  ) : (
                    <span className='text-2xl font-bold text-slate-900'>
                      {row.checkValue}
                      {decimal}
                    </span>
                  )}
                  {row.efosConnectAt && (
                    <span className='text-slate-700'>
                      {dayjs(row.efosConnectAt).format('YYYY-MM-DD')}
                    </span>
                  )}
                </div>
              </div>

              {/* 이미지 썸네일 */}
              {imageSrc && (
                <div className='shrink-0 self-center' style={{width: '38%', maxWidth: 150}}>
                  <img
                    src={imageSrc}
                    alt='meter'
                    className='w-full h-auto object-contain rounded cursor-pointer border-2 border-slate-400'
                    onClick={() => onImageDetailOpen(row)}
                  />
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default CustomerStatusMobileCard
